import { FieldArray, useField, useFormikContext } from "formik";
import React, { useEffect } from "react";
import { useCopies } from "shared/hooks/useCopies";
import FormLabel from "shared/form/fields/formLabel";
import EntityNameSearchField from "shared/form/fields/entityNameSearchField";
import { ApiEventParticipantSearchResult } from "shared/types/ApiEventParticipantSearchResult";
import { HttpVerb } from "shared/utils/enums";
import ConsoleApi from "apps/console/enums/consoleApi";
import Button from "shared/components/button";
import Icon from "assets/icons";
import ErrorField from "shared/form/fields/errorField";
import { ApiEventParticipant } from "shared/types/ApiEventParticipant";
import { ApiEventParticipantForContactContext } from "shared/types/contact/ApiEventParticipantForContactContext";
import { MaterialTableColumn } from "shared/components/materialTable/types";
import MaterialTable from "shared/components/materialTable";
import { getCommonColumnsForContactContext, getParticipantsFromParticipantIds } from "shared/contactContext/utils";
import { AddParticipantInConsoleContextProps } from "shared/contactContext/types";
import { DEFAULT_MAX_RESULTS_COUNT_FOR_SEARCH_CONTACT_CONTEXT } from "shared/utils/constants";
import { errorToast } from "shared/utils/toastNotification";

export function AddParticipantInConsoleContext(props: AddParticipantInConsoleContextProps) {
  const { setFieldValue, values, setFieldTouched } = useFormikContext<any>();
  const [, meta] = useField({
    name: props.name
  });
  const [map, setMap] = React.useState<Record<string, ApiEventParticipantForContactContext | ApiEventParticipant>>(
    props.participantMap
  );
  const copies = useCopies();
  const _add_new = `${props.name}_add_new`;
  const _eventParticipant = `${props.name}_eventParticipant`;
  useEffect(() => {
    setMap(m => {
      return {
        ...m,
        ...props.participantMap
      };
    });
  }, [props.participantMap]);

  return (
    <FieldArray name={props.name}>
      {({ push, remove }) => {
        return (
          <div className="addViewEntriesCombo">
            <div className="addViewEntriesCombo_title">
              <FormLabel label={copies.get(props.label)} />
            </div>
            <div className="addViewEntriesCombo_wrapper">
              <div className="addViewEntriesCombo_addEntries">
                <EntityNameSearchField<ApiEventParticipantSearchResult>
                  disabled={props.disabled}
                  label=""
                  fetchType={HttpVerb.GET}
                  requestBodyObj={{
                    eventId: props.eventId,
                    resultCount: DEFAULT_MAX_RESULTS_COUNT_FOR_SEARCH_CONTACT_CONTEXT
                  }}
                  getResponseParser={(res: any) => getResponseParser(res, copies.get("empty.cell"))}
                  name={_add_new}
                  formEntityIdKeyName={_eventParticipant}
                  placeholder={copies.get("contact.context.assign.search.by.name.or.org.name")}
                  apiUrl={ConsoleApi.GET_EVENT_PARTICIPANTS_BY_NAME_OR_ORG_NAME}
                  resetHandler={() => {
                    setFieldValue(_eventParticipant, "");
                  }}
                  selectHandler={item => {
                    setFieldValue(_add_new, item.name);
                    setFieldValue(_eventParticipant, item);
                  }}
                  resetFieldWhenRemoved
                />
                <Button.Add
                  className="addViewEntriesCombo_addEntries--btn"
                  disabled={!_eventParticipant || props.disabled}
                  onClick={() => {
                    const p = values[_eventParticipant];
                    if (p) {
                      setMap({ ...map, [p.id]: p });
                    }
                    if (!props.currentParticipantIds.includes(p.id) && p.id) {
                      push(p.id);
                    } else {
                      p.id && errorToast(copies.get("contact.context.user.already.in.list"));
                    }
                    setFieldValue(_add_new, "");
                    setFieldTouched(props.name, true);
                    setFieldValue(_eventParticipant, "");
                  }}
                  isOutline
                />
              </div>
              <div className="addViewEntriesCombo_viewEntries">
                <AdminTable
                  disabled={props.disabled}
                  data={getParticipantsFromParticipantIds(map, props.currentParticipantIds)}
                  currentParticipantIds={props.currentParticipantIds}
                  removeHandler={remove}
                />
              </div>
              <ErrorField meta={meta} />
            </div>
          </div>
        );
      }}
    </FieldArray>
  );
}

function AdminTable(props: {
  data: Array<ApiEventParticipant | ApiEventParticipantForContactContext>;
  currentParticipantIds: Array<string>;
  removeHandler: (index: number) => void;
  disabled: boolean;
}) {
  const copies = useCopies();
  const columns = React.useMemo(() => {
    const columnList: Array<MaterialTableColumn<ApiEventParticipant | ApiEventParticipantForContactContext>> = [
      ...getCommonColumnsForContactContext(copies),
      {
        name: copies.get("action"),
        Cell: participant => (
          <Button.Delete
            className="leadParticipantCanRemove"
            disabled={props.disabled}
            label={""}
            icon={<Icon.RemoveCircle size={19} />}
            onClick={e => {
              e.preventDefault();
              const indexToRemove = props.currentParticipantIds.indexOf(participant.id);
              props.removeHandler(indexToRemove);
            }}
          />
        ),
        width: 7
      }
    ];
    return columnList;
  }, [props, copies]);
  return (
    <div className="addViewEntriesCombo_viewEntriesWrapper">
      <MaterialTable<ApiEventParticipant | ApiEventParticipantForContactContext>
        columns={columns}
        data={props.data}
        hideShadow
        showInlineNoResult
      />
    </div>
  );
}

function getResponseParser(res: any, emptyCopy: string) {
  return res?.map((item: any) => ({
    ...item,
    name: `${item?.firstName} ${item.lastName} | ${item?.jobTitle ?? emptyCopy} | ${item?.eventOrganization}`,
    firstName: item?.firstName,
    lastName: item?.lastName
  }));
}
